import * as yup from 'yup';
import { setLocale } from 'yup';
import { createDefaultAddressData, PropertyAddressSchemaGenerator } from './AddressSchema';
import { DateTime } from 'luxon';
import { PartySchemaGenerator } from './PartySchema';
import { v4 } from 'uuid';
import { eSolarPanels } from 'ui-toolkit/orders/interfaces/eSolarPanels';
import { eOrderGroupCategory } from '../../../ui-toolkit/enums/eOrderGroupCategory';
import { MobilityGroupSchemaGenerator, MobilitySchemaGenerator } from './MobilitySchema';
import { IMobilityGroup } from '../../../types/mobility/IMobilityGroup';
import { eServices } from '../../../ui-toolkit/enums/eServices';
import { eSplitClosing } from '../../../ui-toolkit/enums/eSplitClosing';
import { eTransactionCategory } from 'ui-toolkit/enums/eTransactionType';
import { eAccessControlUserType } from 'ui-toolkit/enums/eAccessControlUserType';
import { ePartyType } from '../../../ui-toolkit/enums/ePartyType';
import { ePartyPurpose } from '../../../ui-toolkit/enums/ePartyPurpose';
import { ePropertyType } from '../../../ui-toolkit/orders/interfaces/ePropertyType';
import { eOccupancyType } from '../../../ui-toolkit/orders/interfaces/eOccupancyType';
import { eLoanType } from '../../../ui-toolkit/orders/interfaces/eLoanType';

// Sub Schemas are functions so that this locale will apply. yup applies locale to things when it's called, not when it's used.
setLocale({
  mixed: {
    required: 'This is required',
  },
  string: {
    email: 'Enter a valid email',
    // eslint-disable-next-line
    min: 'Enter ${min} digits',
    // eslint-disable-next-line
    max: 'Enter ${max} digits',
  },
  number: {},
});

export const generateNewOrderSchema = (mobilityGroups: IMobilityGroup[]) => {
  return yup.object({
    groupId: yup.string().nullable(),
    groupCategory: yup.string().nullable(),
    propertyAddress: PropertyAddressSchemaGenerator(),
    solarPanels: yup.mixed<eSolarPanels>().nullable(),
    loanId: yup.string().nullable(),
    externalPartyTransactionId: yup.string().nullable(),
    transactionCategory: yup
      .mixed<eTransactionCategory>()
      .required()
      .oneOf([eTransactionCategory.Refinance, eTransactionCategory.Purchase])
      .default(eTransactionCategory.Refinance),
    propertyType: yup.mixed<ePropertyType>().nullable(),
    occupancyType: yup.mixed<eOccupancyType>().nullable(),
    loanType: yup.mixed<eLoanType>().nullable(),
    loanAmount: yup
      .number()
      .nullable()
      .when('transactionCategory', {
        is: eTransactionCategory.Refinance,
        //FYI: using typeError to catch that null is NaN and masking default error message as a more helpful "required" error message
        then: yup.number().required().typeError('This is required'),
        otherwise: yup.number().nullable(),
      }),
    estimatedPropertyValue: yup.number().nullable(),
    purchasePrice: yup
      .number()
      .nullable()
      .when('transactionCategory', {
        is: eTransactionCategory.Purchase,
        //FYI: using typeError to catch that null is NaN and masking default error message as a more helpful "required" error message
        then: yup.number().required().typeError('This is required'),
        otherwise: yup.number().nullable(),
      }),
    estimatedClosingDate: yup
      .string()
      .test(
        'estimated-closing-date-test',
        'Enter a valid date',
        (value: string | undefined) => !value || DateTime.fromISO(value).isValid
      )
      .test(
        'estimated-closing-date-not-past',
        'Date cannot be in the past',
        (value: string | undefined) => {
          if (!value) return true;
          const date = DateTime.fromISO(value);
          return date.isValid && date >= DateTime.now().startOf('day');
        }
      )
      .nullable(),
    rateLockExpiration: yup
      .string()
      .test(
        'rateLockExpiration',
        'Enter a valid date',
        (value: string | undefined) => !value || DateTime.fromISO(value).isValid
      )
      .test(
        'rateLockExpiration-not-past',
        'Date cannot be in the past',
        (value: string | undefined) => {
          if (!value) return true;
          const date = DateTime.fromISO(value);
          return date.isValid && date >= DateTime.now().startOf('day');
        }
      )
      .nullable(),
    relocationEmployerName: yup.string().nullable(),
    closingDate: yup
      .string()
      .test(
        'closing-date-test',
        'Enter a valid date',
        (value: string | undefined) => !value || DateTime.fromISO(value).isValid
      )
      .test(
        'closing-date-not-past',
        'Date cannot be in the past',
        (value: string | undefined) => {
          if (!value) return true;
          const date = DateTime.fromISO(value);
          return date.isValid && date >= DateTime.now().startOf('day');
        }
      )
      .nullable(),
    lenderCompanyId: yup.string().required().nullable(),
    clientCompanyId: yup
      .string()
      .when('userType', {
        is: (userType: string) => userType !== eAccessControlUserType.Client,
        then: yup.string().required().nullable(),
      })
      .nullable(),
    parties: yup.array(PartySchemaGenerator()),
    mobility: yup
      .object({ transactionType: yup.string() })
      .when(['transactionCategory', 'groupCategory', 'mobilityGroup'], {
        is: (trans_cat: eTransactionCategory, group_cat: string, group: { id: string | null }) =>
          trans_cat === eTransactionCategory.Purchase &&
          group_cat === eOrderGroupCategory.Mobility &&
          group &&
          !group.id,
        then: MobilitySchemaGenerator(),
        otherwise: yup.object().nullable(),
      })
      .nullable(),
    mobilityGroup: yup
      .object()
      .when(['transactionCategory', 'groupCategory'], {
        is: (trans_cat: eTransactionCategory, group_cat: string) =>
          trans_cat === eTransactionCategory.Purchase && group_cat === eOrderGroupCategory.Mobility,
        then: MobilityGroupSchemaGenerator(mobilityGroups),
        otherwise: yup.object().nullable(),
      })
      .nullable(),
    splitClosing: yup
      .mixed<eSplitClosing>()
      .required()
      .oneOf([eSplitClosing.no, eSplitClosing.buyer, eSplitClosing.seller])
      .default(eSplitClosing.no),
    services: yup.array().of(yup.mixed<eServices>()).required().min(1).default([eServices.title, eServices.escrow]),
  });
};

const defaultSchema = generateNewOrderSchema([]);
export type INewOrderSchema = yup.InferType<typeof defaultSchema>;

export const createDefaultNewOrderData = (
  userType: eAccessControlUserType,
  lenderCompanyId: string | null = null,
  clientCompanyId: string | null = null
): INewOrderSchema & { userType: eAccessControlUserType } => ({
  closingDate: null,
  estimatedClosingDate: null,
  estimatedPropertyValue: null,
  externalPartyTransactionId: null,
  loanAmount: null,
  loanId: null,
  loanType: null,
  occupancyType: null,
  groupId: null,
  groupCategory: null,
  propertyAddress: createDefaultAddressData(),
  propertyType: null,
  purchasePrice: null,
  rateLockExpiration: null,
  relocationEmployerName: null,
  solarPanels: null,
  transactionCategory: eTransactionCategory.Purchase,
  lenderCompanyId: lenderCompanyId!,
  clientCompanyId: clientCompanyId ?? lenderCompanyId,
  userType: userType,
  mobility: null,
  mobilityGroup: null,
  parties: [
    {
      id: v4(),
      type: ePartyType.person,
      purpose: ePartyPurpose.buyer,
      firstName: null,
      middleName: null,
      lastName: null,
      email: null,
      ssn: null,
      maritalStatus: '',
      phoneNumber: null,
      entityName: null,
      entityType: null,
      dateOfBirth: null,
      address: null,
      signatories: [],
    },
  ],
  splitClosing: eSplitClosing.no,
  services: [eServices.title, eServices.escrow],
});
